import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DollarSign, Percent, Calendar, PiggyBank, TrendingUp } from 'lucide-react';
import FAQ from './FAQ';

interface CalculatorInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  icon: React.ReactNode;
  prefix?: string;
  suffix?: string;
}

const CalculatorInput: React.FC<CalculatorInputProps> = ({ label, value, onChange, icon, prefix, suffix }) => (
  <div className="relative">
    <label className="block text-sm font-medium text-gray-900 mb-1">{label}</label>
    <div className="relative rounded-md shadow-sm">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-emerald-700">
        {icon}
      </div>
      {prefix && (
        <div className="absolute inset-y-0 left-8 pl-3 flex items-center pointer-events-none">
          <span className="text-gray-700 sm:text-sm">{prefix}</span>
        </div>
      )}
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="block w-full pl-12 pr-12 py-2 rounded-md border-gray-300 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
      />
      {suffix && (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <span className="text-gray-700 sm:text-sm">{suffix}</span>
        </div>
      )}
    </div>
  </div>
);

const Calculator: React.FC = () => {
  const [initialInvestment, setInitialInvestment] = useState(10000);
  const [monthlyContribution, setMonthlyContribution] = useState(500);
  const [annualReturn, setAnnualReturn] = useState(8);
  const [years, setYears] = useState(20);

  const calculatedData = useMemo(() => {
    const monthlyRate = annualReturn / 100 / 12;
    const months = years * 12;
    const data = [];

    let balance = initialInvestment;
    let totalContributed = initialInvestment;

    for (let i = 0; i <= months; i++) {
      if (i > 0) {
        balance = balance * (1 + monthlyRate) + monthlyContribution;
        totalContributed += monthlyContribution;
      }

      if (i % 12 === 0) {
        data.push({
          year: i / 12,
          balance: Math.round(balance),
          contributed: Math.round(totalContributed),
        });
      }
    }

    return data;
  }, [initialInvestment, monthlyContribution, annualReturn, years]);

  const finalBalance = calculatedData[calculatedData.length - 1]?.balance || 0;
  const totalContributed = calculatedData[calculatedData.length - 1]?.contributed || 0;
  const totalEarnings = finalBalance - totalContributed;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-6 sm:p-8">
          <header className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Investment Growth Calculator</h1>
            <p className="mt-2 text-gray-700">Plan your financial future with our compound interest calculator</p>
          </header>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Investment Parameters</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <CalculatorInput
                label="Initial Investment"
                value={initialInvestment}
                onChange={setInitialInvestment}
                icon={<PiggyBank size={20} />}
                prefix="$"
              />
              <CalculatorInput
                label="Monthly Contribution"
                value={monthlyContribution}
                onChange={setMonthlyContribution}
                icon={<DollarSign size={20} />}
                prefix="$"
              />
              <CalculatorInput
                label="Annual Return"
                value={annualReturn}
                onChange={setAnnualReturn}
                icon={<TrendingUp size={20} />}
                suffix="%"
              />
              <CalculatorInput
                label="Years of Growth"
                value={years}
                onChange={setYears}
                icon={<Calendar size={20} />}
                suffix="years"
              />
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Investment Results</h2>
            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-4 shadow">
                  <h3 className="text-lg font-medium text-gray-900">Final Balance</h3>
                  <p className="mt-2 text-2xl font-semibold text-emerald-700">{formatCurrency(finalBalance)}</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow">
                  <h3 className="text-lg font-medium text-gray-900">Total Contributed</h3>
                  <p className="mt-2 text-2xl font-semibold text-emerald-700">{formatCurrency(totalContributed)}</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow">
                  <h3 className="text-lg font-medium text-gray-900">Total Earnings</h3>
                  <p className="mt-2 text-2xl font-semibold text-emerald-700">{formatCurrency(totalEarnings)}</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Growth Projection</h2>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={calculatedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" name="Year" />
                  <YAxis tickFormatter={(value) => `$${value.toLocaleString()}`} />
                  <Tooltip
                    formatter={(value: number) => [`$${value.toLocaleString()}`, '']}
                    labelFormatter={(label) => `Year ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="balance"
                    stroke="#047857"
                    strokeWidth={2}
                    name="Balance"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="contributed"
                    stroke="#065f46"
                    strokeWidth={2}
                    name="Contributed"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Calculator;